// src/products/dataroom/components/BiddingGroupAnalysis.js
// Component to analyze and manage the relationship between bidding groups and dataroom groups

import React, { useState, useEffect, useCallback } from 'react';
import { 
  Users, 
  Building, 
  Database, 
  Link, 
  AlertCircle, 
  CheckCircle, 
  Info,
  ArrowRight,
  ArrowLeft
} from 'lucide-react';
import { dataroomInviteIntegration } from '../services/dataroomInviteIntegration';

const BiddingGroupAnalysis = ({ projectId, projectName, onMessage }) => {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [recommendation, setRecommendation] = useState(null);

  const analyzeBiddingGroupIntegration = useCallback(async () => {
    try {
      setLoading(true);
      const result = await dataroomInviteIntegration.analyzeBiddingGroupIntegration(projectId);
      
      if (result.status === 'success') {
        setAnalysis(result.data);
        generateRecommendation(result.data);
      } else {
        onMessage && onMessage('Failed to analyze bidding group integration: ' + result.message, 'error');
      }
    } catch (error) {
      console.error('Error analyzing bidding groups:', error);
      onMessage && onMessage('Failed to analyze bidding groups', 'error');
    } finally {
      setLoading(false);
    }
  }, [projectId, onMessage]);

  useEffect(() => {
    if (projectId) {
      analyzeBiddingGroupIntegration();
    }
  }, [projectId, analyzeBiddingGroupIntegration]);

  const generateRecommendation = (data) => {
    const hasCapRaiseIntegration = data.hasCapRaiseIntegration;
    
    if (hasCapRaiseIntegration) {
      setRecommendation({
        type: 'separate',
        title: 'Use Separate Groups',
        description: 'Keep bidding groups separate from dataroom groups for better organization',
        reasoning: [
          'CapRaise integration detected - different access patterns needed',
          'Investors need specific bidding group access for Q&A',
          'Dataroom groups can focus on document permissions',
          'Better separation of concerns between fundraising and document management'
        ],
        suggestions: [
          'Create bidding-specific groups for investor access',
          'Use dataroom groups for document-level permissions',
          'Link bidding groups to dataroom groups when needed',
          'Consider automatic group assignment based on investor status'
        ]
      });
    } else {
      setRecommendation({
        type: 'unified',
        title: 'Unified Groups Approach',
        description: 'Use dataroom groups for both document access and potential bidding',
        reasoning: [
          'No CapRaise integration detected',
          'Simpler management with unified groups',
          'Groups can serve dual purpose for future expansion',
          'Easier user management and permissions'
        ],
        suggestions: [
          'Create general-purpose groups (e.g., "Investors", "Advisors")',
          'Set up document permissions based on group membership',
          'Plan for future CapRaise integration',
          'Consider group naming that works for both purposes'
        ]
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Analyzing bidding group integration...</span>
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className="text-center py-8 text-gray-500">
        <AlertCircle className="h-12 w-12 text-gray-300 mx-auto mb-3" />
        <p>Unable to load analysis</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-3">
        <div className="p-2 bg-purple-100 rounded-lg">
          <Users className="h-6 w-6 text-purple-600" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Bidding Groups vs Dataroom Groups</h3>
          <p className="text-sm text-gray-600">Analysis for {projectName}</p>
        </div>
      </div>

      {/* Integration Status */}
      <div className={`p-4 rounded-lg border ${
        analysis.hasCapRaiseIntegration 
          ? 'bg-green-50 border-green-200' 
          : 'bg-yellow-50 border-yellow-200'
      }`}>
        <div className="flex items-center space-x-3">
          {analysis.hasCapRaiseIntegration ? (
            <CheckCircle className="h-5 w-5 text-green-600" />
          ) : (
            <AlertCircle className="h-5 w-5 text-yellow-600" />
          )}
          <div>
            <h4 className="font-medium text-gray-900">
              {analysis.hasCapRaiseIntegration ? 'CapRaise Integration Detected' : 'No CapRaise Integration'}
            </h4>
            <p className="text-sm text-gray-600">
              {analysis.hasCapRaiseIntegration 
                ? 'This dataroom is linked to CapRaise fundraising rounds'
                : 'This dataroom operates independently of CapRaise'
              }
            </p>
          </div>
        </div>
      </div>

      {/* CapRaise Links */}
      {analysis.hasCapRaiseIntegration && analysis.dealroomLinks && (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h4 className="font-medium text-gray-900 mb-3 flex items-center">
            <Link className="h-4 w-4 mr-2" />
            Linked CapRaise Rounds
          </h4>
          <div className="space-y-2">
            {analysis.dealroomLinks.map((link, index) => (
              <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                <div className="flex items-center space-x-2">
                  <Building className="h-4 w-4 text-blue-600" />
                  <span className="text-sm font-medium">
                    {link.capraise_rounds?.round_name || 'Unknown Round'}
                  </span>
                </div>
                <div className="text-xs text-gray-500">
                  {link.capraise_rounds?.round_type} • {link.capraise_rounds?.status}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recommendation */}
      {recommendation && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-start space-x-3">
            <div className={`p-2 rounded-lg ${
              recommendation.type === 'separate' ? 'bg-blue-100' : 'bg-green-100'
            }`}>
              {recommendation.type === 'separate' ? (
                <ArrowRight className="h-5 w-5 text-blue-600" />
              ) : (
                <ArrowLeft className="h-5 w-5 text-green-600" />
              )}
            </div>
            <div className="flex-1">
              <h4 className="text-lg font-semibold text-gray-900 mb-2">
                {recommendation.title}
              </h4>
              <p className="text-gray-600 mb-4">
                {recommendation.description}
              </p>

              {/* Reasoning */}
              <div className="mb-4">
                <h5 className="font-medium text-gray-900 mb-2">Why this approach?</h5>
                <ul className="space-y-1">
                  {recommendation.reasoning.map((reason, index) => (
                    <li key={index} className="text-sm text-gray-600 flex items-start">
                      <span className="text-blue-500 mr-2">•</span>
                      {reason}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Suggestions */}
              <div>
                <h5 className="font-medium text-gray-900 mb-2">Implementation suggestions:</h5>
                <ul className="space-y-1">
                  {recommendation.suggestions.map((suggestion, index) => (
                    <li key={index} className="text-sm text-gray-600 flex items-start">
                      <span className="text-green-500 mr-2">✓</span>
                      {suggestion}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Architecture Diagram */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h4 className="font-medium text-gray-900 mb-4 flex items-center">
          <Info className="h-4 w-4 mr-2" />
          Recommended Architecture
        </h4>
        
        <div className="flex items-center justify-center space-x-8">
          {/* Bidding Groups */}
          <div className="text-center">
            <div className="p-3 bg-purple-100 rounded-lg mb-2">
              <Users className="h-8 w-8 text-purple-600 mx-auto" />
            </div>
            <h5 className="font-medium text-gray-900">Bidding Groups</h5>
            <p className="text-xs text-gray-600">CapRaise Q&A access</p>
          </div>

          {/* Arrow */}
          <ArrowRight className="h-5 w-5 text-gray-400" />

          {/* Dataroom Groups */}
          <div className="text-center">
            <div className="p-3 bg-blue-100 rounded-lg mb-2">
              <Database className="h-8 w-8 text-blue-600 mx-auto" />
            </div>
            <h5 className="font-medium text-gray-900">Dataroom Groups</h5>
            <p className="text-xs text-gray-600">Document permissions</p>
          </div>

          {/* Arrow */}
          <ArrowRight className="h-5 w-5 text-gray-400" />

          {/* Users */}
          <div className="text-center">
            <div className="p-3 bg-green-100 rounded-lg mb-2">
              <Building className="h-8 w-8 text-green-600 mx-auto" />
            </div>
            <h5 className="font-medium text-gray-900">Users</h5>
            <p className="text-xs text-gray-600">Can be in both</p>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-end space-x-3">
        <button
          onClick={analyzeBiddingGroupIntegration}
          className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          Refresh Analysis
        </button>
        <button
          onClick={() => window.open('/capraise', '_blank')}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
        >
          View CapRaise Integration
        </button>
      </div>
    </div>
  );
};

export default BiddingGroupAnalysis;
